// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Settings > "Open Konvo on". Where the app lands when it starts on the
// root URL: "Last used" or one of this device's destinations. The phone offers
// its rail tabs (shown in Settings > Mobile rail), the desktop its sidebar pages
// (Settings > Appearance). Saved on this device, per form factor.

import {
	type SettingsIconChoiceCardOption,
	SettingsIconChoiceCards,
} from '@app/features/app/components/dialogs/shared/SettingsIconChoiceCards';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {
	MOBILE_RAIL_TAB_LABELS,
	MobileRailTabIcon,
} from '@app/features/app/components/layout/mobile_rail/MobileRailTabLabels';
import {SIDEBAR_PAGE_LABELS, SidebarPageIcon} from '@app/features/app/components/layout/sidebar_nav/SidebarPageLabels';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import StartPage from '@app/features/navigation/state/StartPage';
import {startOptions} from '@app/features/navigation/utils/StartPageLaunch';
import {
	DEFAULT_START_DESTINATION,
	effectiveStartChoice,
	type StartChoice,
	type StartDestination,
	type StartFormFactor,
} from '@app/features/navigation/utils/StartPageModel';
import {ClockCounterClockwiseIcon} from '@app/features/ui/icons/Icons';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Open Konvo on',
	comment: 'Settings section title: which page the app shows when it starts.',
});
const PHONE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'The tab Konvo shows when you open the app on this phone.',
	comment: 'Description under "Open Konvo on" in Settings > Mobile rail.',
});
const DESKTOP_DESCRIPTION_DESCRIPTOR = msg({
	message: 'The page Konvo shows when you start it on this computer.',
	comment: 'Description under "Open Konvo on" in Settings > Appearance on desktop.',
});
const LAST_USED_DESCRIPTOR = msg({
	message: 'Last used',
	comment: 'Option in "Open Konvo on": reopen wherever the user was last. Keep it short.',
});
const LAST_USED_NOTE_DESCRIPTOR = msg({
	message: 'Konvo reopens where you left off. Last time that was {label}.',
	comment: 'Note under the "Open Konvo on" choices when Last used is picked. {label} is a page name like Mail.',
});
const LAST_USED_EMPTY_NOTE_DESCRIPTOR = msg({
	message: 'Konvo reopens where you left off, starting with Chats.',
	comment: 'Note under the "Open Konvo on" choices when Last used is picked and nothing was used yet.',
});
const DESTINATION_NOTE_DESCRIPTOR = msg({
	message: 'Konvo opens on {label}. A notification or link to a chat still opens that chat. Saved on this device.',
	comment: 'Note under the "Open Konvo on" choices when a fixed page is picked. {label} is a page name like Home.',
});
const UNAVAILABLE_NOTE_DESCRIPTOR = msg({
	message: '{label} is not available here any more, so Konvo opens on Chats.',
	comment:
		'Note under the "Open Konvo on" choices when the saved page was removed (e.g. taken off the phone rail). {label} is a page name.',
});
const ARIA_DESCRIPTOR = msg({
	message: 'Page to open Konvo on',
	comment: 'Accessible label for the "Open Konvo on" choice cards.',
});

const ICON_SIZE = 20;

function labelFor(destination: StartDestination, formFactor: StartFormFactor): MessageDescriptor {
	if (formFactor === 'desktop' && destination !== 'scratchpad') return SIDEBAR_PAGE_LABELS[destination];
	return MOBILE_RAIL_TAB_LABELS[destination];
}

function iconFor(destination: StartDestination, formFactor: StartFormFactor): React.ReactNode {
	if (formFactor === 'desktop' && destination !== 'scratchpad')
		return <SidebarPageIcon page={destination} size={ICON_SIZE} />;
	return <MobileRailTabIcon tab={destination} size={ICON_SIZE} />;
}

interface StartPageSectionProps {
	formFactor: StartFormFactor;
	'data-flx'?: string;
}

export const StartPageSection = observer(({formFactor, 'data-flx': dataFlx}: StartPageSectionProps) => {
	const {i18n} = useLingui();
	useEffect(() => {
		// Both lists are account-synced: the phone's rail tabs and the desktop's sidebar pages.
		void KonvoCalendarPrefs.load();
	}, [formFactor]);
	const destinations = startOptions(formFactor);
	const saved = StartPage.choiceFor(formFactor);
	const value = effectiveStartChoice(saved);
	const remembered = StartPage.lastFor(formFactor);

	const options: Array<SettingsIconChoiceCardOption<StartChoice>> = [
		{value: 'last', name: i18n._(LAST_USED_DESCRIPTOR), icon: <ClockCounterClockwiseIcon size={ICON_SIZE} />},
		...destinations.map((destination) => ({
			value: destination,
			name: i18n._(labelFor(destination, formFactor)),
			icon: iconFor(destination, formFactor),
		})),
	];

	let note: string;
	if (value === 'last') {
		note = remembered
			? i18n._(LAST_USED_NOTE_DESCRIPTOR, {label: i18n._(labelFor(remembered, formFactor))})
			: i18n._(LAST_USED_EMPTY_NOTE_DESCRIPTOR);
	} else if (value !== DEFAULT_START_DESTINATION && !destinations.includes(value)) {
		note = i18n._(UNAVAILABLE_NOTE_DESCRIPTOR, {label: i18n._(labelFor(value, formFactor))});
	} else {
		note = i18n._(DESTINATION_NOTE_DESCRIPTOR, {label: i18n._(labelFor(value, formFactor))});
	}

	return (
		<SettingsSection
			id="start-page"
			tabType={formFactor === 'phone' ? 'mobile_rail' : 'appearance'}
			title={i18n._(TITLE_DESCRIPTOR)}
			description={i18n._(formFactor === 'phone' ? PHONE_DESCRIPTION_DESCRIPTOR : DESKTOP_DESCRIPTION_DESCRIPTOR)}
			linkable={formFactor === 'desktop'}
			data-flx={dataFlx}
		>
			<SettingsIconChoiceCards<StartChoice>
				options={options}
				value={value}
				onChange={(next) => StartPage.setChoice(formFactor, next)}
				aria-label={i18n._(ARIA_DESCRIPTOR)}
				note={note}
				data-flx="user.start-page-section.choices"
			/>
		</SettingsSection>
	);
});
